import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewRef, ChangeDetectionStrategy, signal } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { debounceTime, Subject, takeUntil } from 'rxjs';

import { AppStateService } from 'src/app/core-module/services/app-state.service';
import { Options } from '@angular-slider/ngx-slider';
import { AloAocStateService } from "../../../core-module/services/state/alo-aoc-state.service";
import { CheckboxStateService } from "../../../core-module/services/state/checkbox-state.service";
import { FeatureDefaultsStateService } from "../../../core-module/services/state/feature-defaults-state.service";
import { InputErrorsStateService } from "../../../core-module/services/state/input-errors-state.service";
@Component({
  standalone: false,
  selector: 'app-aoc-multichannel',
  templateUrl: './aoc-multichannel.component.html',
  styleUrls: ['./aoc-multichannel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AocMultichannelComponent extends FieldType implements OnInit, OnDestroy {
  // Feature enable state
  isEnableFeature: boolean;
  private readonly aocDisabledSignal = signal(false);
  showEnableFeature = false;
  column: LegacyAny;

  // Number of channels
  nrOfChannels: number;
  nrOfChannelsMin: number;
  nrOfChannelsMax: number;

  // Channel configurations - dynamic array
  channels: {
    value: number;
    min: number;
    max: number;
    default: number;
    displayValue: LegacyAny;
    options: Options;
  }[] = [];

  // Default values from schema
  private isEnableFeatureDefault: boolean;
  private nrOfChannelsDefault: number;

  // Error tracking
  inputError: LegacyAny = {};
  private readonly aocDisabledButtonSignal = signal(false);

  // Default color tracking
  private readonly defaultColorSignal = signal(true);

  // Standard/Advanced mode
  quickConfigvalue: boolean;
  quickConfigCustomCSS: string;
  setQuickConfig: string;

  // Include in configuration tracking
  private readonly isIncludedInConfigurationSignal = signal(false);
  private ignoreParentUpdate = false;
  private hasConfigData = false;
  private isResetting = false;

  // Input subjects for debouncing
  private channelInputSubjects: Subject<{ value: string; index: number }>[] = [];

  private properties: LegacyAny;
  private readonly destroy$ = new Subject<void>();

  get aocDisabled(): boolean { return this.aocDisabledSignal(); }
  set aocDisabled(value: boolean) { this.aocDisabledSignal.set(!!value); }

  get aocDisabledButton(): boolean { return this.aocDisabledButtonSignal(); }
  set aocDisabledButton(value: boolean) { this.aocDisabledButtonSignal.set(!!value); }

  get defaultColor(): boolean { return this.defaultColorSignal(); }
  set defaultColor(value: boolean) { this.defaultColorSignal.set(!!value); }

  get isIncludedInConfiguration(): boolean { return this.isIncludedInConfigurationSignal(); }
  set isIncludedInConfiguration(value: boolean) { this.isIncludedInConfigurationSignal.set(!!value); }

  constructor(
    
    private readonly ref: ChangeDetectorRef,
    private readonly appStateService: AppStateService,
    private featureDefaults: FeatureDefaultsStateService,
    private checkboxState: CheckboxStateService,
    private inputErrors: InputErrorsStateService,
    private aloAoc: AloAocStateService
  ) {
    super();
  }

  ngOnInit(): void {
    this.ignoreParentUpdate = true;

    this.properties = (this.field as LegacyAny)?.properties;
    this.column = (this.field as LegacyAny).requiredProperties;
    this.showEnableFeature = this.column?.includes('Enabled') ?? false;

    if (this.properties) {
      this.loadFromSchema();
    }

    if (!this.showEnableFeature) {
      this.aocDisabled = false;
    }

    this.initializeSliderOptions();

    // Load saved configuration data
    const configData = this.appStateService.getConfigurationData();
    let savedValue = null;

    const unSelectedFeat = this.appStateService.getUnSelectedFeatConfig();
    let isConfigEdited = false;

    if (unSelectedFeat != null && Array.isArray(unSelectedFeat)) {
      let featIndex = 0;
      unSelectedFeat.forEach((element) => {
        if (element["featureName"] && element["featureName"].toString().toUpperCase() === "AOCMULTICHANNEL") {
          isConfigEdited = true;
          savedValue = element["featureParameters"][0];
          unSelectedFeat.splice(featIndex, 1);
          this.appStateService.setUnSelectedFeatConfig(unSelectedFeat);
        } else {
          featIndex++;
        }
      });
    }

    if (!isConfigEdited) {
      const aocData = this.appStateService.getFeatureData('AocMultiChannel')
        || this.appStateService.getFeatureData('aocmultichannel')
        || this.appStateService.getFeatureData('AOCMULTICHANNEL');
      if (aocData) {
        savedValue = Array.isArray(aocData) ? aocData[0] : aocData;
        this.hasConfigData = savedValue && Object.keys(savedValue).length > 0;
      }
    }

    const currentValue = savedValue || this.formControl.value || {};
    if (currentValue && Object.keys(currentValue).length > 0) {
      this.loadDataFromObject(currentValue);
    }

    this.setupInputDebouncing();

    // Subscribe to restore default trigger
    this.featureDefaults.getAocMultiChannelDefaultValue
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response === "valid") {
          this.defaultColor = true;
          this.resetToDefaults();
          this.featureDefaults.setAocMultiChannelDefaultValue("");
        }
      });

    // Subscribe to parent checkbox changes
    this.checkboxState.getAocMultiChannelParentCheckbox
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (state !== null && !this.ignoreParentUpdate) {
          this.isIncludedInConfiguration = state.checked;

          if (!state.checked) {
            this.hasConfigData = false;
            this.inputErrors.setAocMultiChannelInputError.next(false);
            this.ref.detectChanges();
            return;
          }

          if (this.hasConfigData) {
            this.sendErrorStateToParent();
            return;
          }

          this.updateFormControl();
          this.sendErrorStateToParent();
          this.ref.detectChanges();
        }
      });

    this.enableFeatureChanged(this.isEnableFeature);
    this.revalidateAllFields();
    this.checkDefaults();

    if (!this.defaultColor) {
      this.checkboxState.enableAocMultiChannelCheckbox();
      this.isIncludedInConfiguration = true;
      this.hasConfigData = true;
    }

    this.sendErrorStateToParent();

    // Subscribe to Standard/Advanced mode changes
    this.aloAoc.getquickConfig
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data === 'quickConfig') {
          this.quickConfigvalue = true;
          this.quickConfigCustomCSS = 'quick-custom-inputbox textBoxUnit';
          this.setQuickConfig = 'quickConfig';
        } else if (data === 'standard') {
          this.quickConfigvalue = false;
          this.quickConfigCustomCSS = 'custom-inputbox';
          this.setQuickConfig = 'standard';
        }
        setTimeout(() => {
          if (this.ref && !(this.ref as ViewRef).destroyed) {
            this.ref.detectChanges();
          }
        });
      });

    this.ignoreParentUpdate = false;
  }

  private initializeSliderOptions(): void {
    this.channels.forEach((ch, i) => {
      ch.options = {
        floor: ch.min,
        ceil: ch.max,
        disabled: this.aocDisabled
      };
    });
  }

  private loadFromSchema(): void {
    if (!this.properties) {
      return;
    }

    this.isEnableFeature = this.isEnableFeatureDefault = this.properties.Enabled?.default ?? true;

    this.nrOfChannelsMin = this.properties.NrOfChannels?.minimum ?? 1;
    this.nrOfChannelsMax = this.properties.NrOfChannels?.maximum ?? 6;
    this.nrOfChannels = this.nrOfChannelsDefault = this.properties.NrOfChannels?.default ?? 2;

    this.aocDisabled = !this.isEnableFeature;

    // Build channel array from schema
    this.channels = [];
    for (let i = 1; i <= this.nrOfChannelsMax; i++) {
      const chProp = this.properties[`Channel${i}`];
      if (chProp) {
        const def = chProp.default ?? chProp.minimum ?? 0;
        this.channels.push({
          value: def,
          min: chProp.minimum ?? 0,
          max: chProp.maximum ?? 65535,
          default: def,
          displayValue: def,
          options: {}
        });
      }
    }
  }

  private loadDataFromObject(data: LegacyAny): void {
    if (data.IsEnabled !== undefined) {
      this.isEnableFeature = data.IsEnabled;
    } else if (data.Enabled !== undefined) {
      this.isEnableFeature = data.Enabled;
    }
    if (data.NrOfChannels !== undefined) {
      this.nrOfChannels = data.NrOfChannels;
    }
    // Load channel values
    for (let i = 0; i < this.channels.length; i++) {
      const key = `Channel${i + 1}`;
      if (data[key] !== undefined) {
        this.channels[i].value = data[key];
        this.channels[i].displayValue = data[key];
      }
    }
  }

  private setupInputDebouncing(): void {
    this.channelInputSubjects = [];
    let index = 0;
    for (const _ of this.channels) {
      const subject = new Subject<{ value: string; index: number }>();
      subject.pipe(
        debounceTime(100),
        takeUntil(this.destroy$)
      ).subscribe(({ value, index }) => {
        this.validateAndUpdateChannel(value, index);
      });
      this.channelInputSubjects.push(subject);
      index++;
    }
  }


  enableFeatureChanged(checked: boolean): void {
    this.ignoreParentUpdate = true;
    this.isEnableFeature = checked;
    this.aocDisabled = !checked;

    this.channels.forEach(ch => {
      ch.options = Object.assign({}, ch.options, { disabled: this.aocDisabled });
    });

    this.checkDefaults();
    if (!this.defaultColor) {
      this.checkboxState.enableAocMultiChannelCheckbox();
      this.isIncludedInConfiguration = true;
    }

    this.sendErrorStateToParent();
    this.updateFormControl();
    this.ref.detectChanges();
    this.ignoreParentUpdate = false;
  }

  onNrOfChannelsChange(value: number): void {
    this.ignoreParentUpdate = true;
    this.nrOfChannels = value;
    this.checkDefaults();
    if (!this.defaultColor) {
      this.checkboxState.enableAocMultiChannelCheckbox();
      this.isIncludedInConfiguration = true;
    }
    this.sendErrorStateToParent();
    this.updateFormControl();
    this.ref.detectChanges();
    this.ignoreParentUpdate = false;
  }

  // Slider change handler for a channel
  channelSliderChange(value: number, index: number): void {
    this.ignoreParentUpdate = true;
    const ch = this.channels[index];
    this.inputError[`channel${index}`] = false;
    if (value >= ch.min && value <= ch.max) {
      ch.value = value;
      ch.displayValue = value;
      this.recalcErrorState();
      this.checkDefaults();
      if (!this.defaultColor) {
        this.checkboxState.enableAocMultiChannelCheckbox();
        this.isIncludedInConfiguration = true;
      }
      this.sendErrorStateToParent();
      this.updateFormControl();
      this.ref.detectChanges();
    }
    this.ignoreParentUpdate = false;
  }

  // Input field handler for a channel
  onChannelInput(value: string, index: number): void {
    if (this.channelInputSubjects[index]) {
      this.channelInputSubjects[index].next({ value, index });
    }
  }

  private validateAndUpdateChannel(value: string, index: number): void {
    if (this.isResetting) {
      return;
    }

    this.ignoreParentUpdate = true;
    const numValue = parseInt(value, 10);
    const ch = this.channels[index];

    ch.value = numValue;
    ch.displayValue = numValue;

    if (value === '' || value === '-0' || numValue < ch.min || numValue > ch.max || isNaN(numValue)) {
      this.inputError[`channel${index}`] = true;
    } else {
      this.inputError[`channel${index}`] = false;
    }

    this.checkDefaults();
    if (!this.defaultColor) {
      this.checkboxState.enableAocMultiChannelCheckbox();
      this.isIncludedInConfiguration = true;
    }

    this.recalcErrorState();
    this.sendErrorStateToParent();
    this.updateFormControl();
    this.ref.detectChanges();
    this.ignoreParentUpdate = false;
  }

  private sendErrorStateToParent(): void {
    if (this.isIncludedInConfiguration) {
      this.inputErrors.setAocMultiChannelInputError.next(this.aocDisabledButton);
    } else {
      this.inputErrors.setAocMultiChannelInputError.next(false);
    }
  }

  private validateField(type: string, value: number, min: number, max: number): void {
    if (value === null || value === undefined || isNaN(value) || value < min || value > max) {
      this.inputError[type] = true;
      this.aocDisabledButton = true;
    }
  }

  private revalidateAllFields(): void {
    this.aocDisabledButton = false;
    this.inputError = {};

    this.channels.forEach((ch, i) => {
      if (i < this.nrOfChannels) {
        this.validateField(`channel${i}`, ch.value, ch.min, ch.max);
      }
    });

    this.sendErrorStateToParent();
  }

  private recalcErrorState(): void {
    this.aocDisabledButton = false;
    for (const type in this.inputError) {
      if (this.inputError[type]) {
        this.aocDisabledButton = true;
        break;
      }
    }
  }

  checkDefaults(): void {
    let isDefault = (
      this.isEnableFeature === this.isEnableFeatureDefault &&
      this.nrOfChannels === this.nrOfChannelsDefault
    );
    if (isDefault) {
      for (const ch of this.channels) {
        if (ch.value !== ch.default) {
          isDefault = false;
          break;
        }
      }
    }
    this.defaultColor = isDefault;
    this.featureDefaults.setAocMultiChannelDefaultColor(this.defaultColor);
  }

  resetToDefaults(): void {
    this.isResetting = true;

    const wasIncludedInConfiguration = this.isIncludedInConfiguration;

    this.isEnableFeature = this.isEnableFeatureDefault;
    this.nrOfChannels = this.nrOfChannelsDefault;
    this.aocDisabled = !this.isEnableFeatureDefault;

    this.channels.forEach(ch => {
      ch.value = ch.default;
      ch.displayValue = ch.default;
    });

    this.inputError = {};
    this.aocDisabledButton = false;
    this.defaultColor = true;

    this.initializeSliderOptions();
    this.updateFormControl();
    this.featureDefaults.setAocMultiChannelDefaultColor(this.defaultColor);
    this.inputErrors.setAocMultiChannelInputError.next(false);

    if (wasIncludedInConfiguration) {
      this.isIncludedInConfiguration = true;
      this.checkboxState.enableAocMultiChannelCheckbox();
    }

    this.ref.detectChanges();

    setTimeout(() => {
      this.isResetting = false;
    }, 150);
  }

  private updateFormControl(): void {
    if (!this.formControl) {
      return;
    }

    const value: LegacyAny = {
      IsEnabled: this.isEnableFeature,
      NrOfChannels: this.nrOfChannels
    };
    this.channels.forEach((ch, i) => {
      value[`Channel${i + 1}`] = ch.value;
    });

    this.formControl.setValue(value);
    this.setConfigurationData(value, 'AocMultiChannel');
  }

  private setConfigurationData(value: LegacyAny, keyVal: string): void {
    if (value != null) {
      const obj = Object.assign({}, value);
      this.appStateService.setFeatureData(keyVal, obj);
    }
  }

  getSliderSafeValue(value: number, min: number, max: number): number {
    if (value === null || value === undefined || isNaN(value)){ return min; }
    if (value < min) {
      return min;
    }
    if (value > max) {
      return max;
    }
    return value;
  }

  onFieldBlur(inputEl: HTMLInputElement, index: number): void {
    const raw = inputEl.value;
    const numericValue = parseFloat(raw);
    const cleaned = isNaN(numericValue) ? '' : String(numericValue);
    inputEl.value = '';
    inputEl.value = cleaned;
    this.channels[index].displayValue = isNaN(numericValue) ? '' : numericValue;
  }

  numberOnly(event): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    return !(charCode > 31 && (charCode < 48 || charCode > 57));
  }

  // Generate array for NrOfChannels dropdown
  get nrOfChannelsOptions(): number[] {
    const options = [];
    for (let i = this.nrOfChannelsMin; i <= this.nrOfChannelsMax; i++) {
      options.push(i);
    }
    return options;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
